'use client';

import NotifyModel from '@/components/dialog/smallmodel/NotifyModel';
import dateUtils from '@/utils/dateUtils';
import { ReactNode, useCallback, useState } from 'react';
export type NotificationType = 'success' | 'error' | 'info' | 'warning' | 'question' | 'none';
export type focusType = 'yes' | 'no' | 'cancel';
interface NotifyItem {
  id: number;
  message: string | ReactNode;
  type: NotificationType;
  title?: string;
  yesText?: string;
  noText?: string;
  cancelText?: string;
  onYesClick?: () => void;
  onNoClick?: () => void;
  onCancelClick?: () => void;
  onCloseClick?: () => void;
  autoFocusButton?: focusType;
  closeOnCancel?: boolean; // NEW
  stayOpen?: boolean;
  closeOnYes?: boolean; // NEW: Control whether Yes button closes modal
}
export type TriggerNotifyParams = string | {
  notifyType?: NotificationType;
  notifyMessage: string | string[] | ReactNode;
  title?: string;
  yesText?: string;
  noText?: string;
  cancelText?: string;
  onYes?: () => void;
  onNo?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
  autoFocusButton?: focusType;
  stayOpen?: boolean;
  closeOnCancel?: boolean; // NEW
  closeOnYes?: boolean; // NEW: Control whether Yes button closes modal
};
export type TriggerNotify = (params: TriggerNotifyParams) => void;
const getNotifyMessage = (params: TriggerNotifyParams): string | ReactNode => {
  if (typeof params === 'string' || typeof params === 'object' && !('notifyMessage' in params)) {
    return params as ReactNode;
  }
  const msg = (params as {
    notifyMessage: string | string[] | ReactNode;
  }).notifyMessage;
  return Array.isArray(msg) ? msg.join(', ') : msg;
};
const useNotify = () => {
  const [notifications, setNotifications] = useState<NotifyItem[]>([]);
  const triggerNotify = useCallback((params: TriggerNotifyParams) => {
    const isStringParam = typeof params === 'string';
    const notifyParams = isStringParam ? {
      notifyMessage: params
    } : params;
    const message = getNotifyMessage(params);
    const newNotification: NotifyItem = {
      id: dateUtils.timestamp(),
      message,
      type: isStringParam ? 'info' : notifyParams.notifyType ?? 'info',
      title: isStringParam ? undefined : notifyParams.title,
      yesText: isStringParam ? undefined : notifyParams.yesText,
      noText: isStringParam ? undefined : notifyParams.noText,
      cancelText: isStringParam ? undefined : notifyParams.cancelText,
      onYesClick: isStringParam ? undefined : notifyParams.onYes,
      onNoClick: isStringParam ? undefined : notifyParams.onNo,
      onCancelClick: isStringParam ? undefined : notifyParams.onCancel,
      onCloseClick: isStringParam ? undefined : notifyParams.onClose,
      autoFocusButton: isStringParam ? 'cancel' : notifyParams.autoFocusButton,
      stayOpen: isStringParam ? false : notifyParams.stayOpen,
      closeOnYes: isStringParam ? true : notifyParams.closeOnYes ?? true,
      // NEW: Default to true,
      closeOnCancel: isStringParam ? true : notifyParams.closeOnCancel ?? true
    };
    setNotifications(prev => [...prev, newNotification]);
  }, []);
  const closeNotification = (id: number) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };
  const handleClose = (notification: NotifyItem) => {
    if (!notification.stayOpen) {
      closeNotification(notification.id);
    }
  };
  const NotifyComponent = <>
      {notifications.map(notification => <NotifyModel key={notification.id} title={notification.title ?? ''} open={true} onYes={notification.onYesClick || notification.yesText ? () => {
      notification.onYesClick?.();
      // Only close if closeOnYes is true (default behavior)
      if (notification.closeOnYes) {
        closeNotification(notification.id);
      }
    } : undefined} onNo={notification.onNoClick ? () => {
      notification.onNoClick?.();
      handleClose(notification);
    } : undefined} onCancel={notification.onCancelClick ? () => {
      notification.onCancelClick?.();
      if (notification.closeOnCancel) {
        handleClose(notification);
      }
    } : undefined} onClose={() => {
      notification.onCloseClick?.();
      handleClose(notification);
    }} yesText={notification.yesText ?? 'Ok'} noText={notification.noText} cancelText={notification.cancelText} type={notification.type} description={notification.message} autoFocusButton={notification.autoFocusButton ?? 'cancel'} />)}
    </>;
  const confirmNotify = useCallback((params: {
    notifyMessage: string;
    title?: string;
    yesText?: string;
    noText?: string;
    cancelText?: string;
    onYes?: () => void;
    onNo?: () => void;
    onCancel?: () => void;
    notifyType?: NotificationType;
    stayOpen?: boolean;
    closeOnYes?: boolean; // NEW
    closeOnCancel?: boolean; // NEW
  }) => {
    triggerNotify({
      notifyType: params.notifyType ?? 'question',
      notifyMessage: params.notifyMessage,
      title: params.title,
      yesText: params.yesText ?? 'Yes',
      noText: params.noText ?? 'No',
      cancelText: params.cancelText,
      onYes: params.onYes,
      onNo: params.onNo,
      onCancel: params.onCancel,
      stayOpen: params.stayOpen ?? false,
      closeOnYes: params.closeOnYes ?? true,
      // NEW
      closeOnCancel: params.closeOnCancel ?? true
    });
  }, [triggerNotify]);

  // ADD: Helper for Formik validation errors
  const showValidationErrors = useCallback((errors: Record<string, string>) => {
    const errorMessages = Object.values(errors).filter(error => error && error.trim() !== '');
    if (errorMessages.length > 0) {
      triggerNotify({
        notifyType: 'error',
        notifyMessage: errorMessages.join(', '),
        title: 'Validation Error'
      });
    }
  }, [triggerNotify]);

  // ADD: Quick error helper
  const showError = useCallback((message: string, title: string = 'Error') => {
    triggerNotify({
      notifyType: 'error',
      notifyMessage: message,
      title
    });
  }, [triggerNotify]);
  return {
    triggerNotify,
    confirmNotify,
    NotifyComponent,
    count: notifications.length,
    showValidationErrors,
    showError
  };
};
export default useNotify;